import React, { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

import round from '../../../helpers/round';
import MuiSnackbar from '../../common/MuiSnackbar';

const OutputValue = React.memo(({ value }) => {
  const displayValue = value ?? '-';
  return <div className="layeredOutputValue">{displayValue}</div>;
});

const OutputLayer = ({
  layer,
  index,
  setLayers,
  resultsObj,
  resultConst,
  isLoadingCalculate,
  isLoadingLoad,
  isLoadingSave,
  isExpanded,
  onToggle,
  canUserSeeCalculationSteps,
}) => {
  const regexForPositiveDecimals = /^[+]?\d*(?:[.]\d*)?$/;

  const onChangeWarningForPositiveDecimals = (value, variable, variableSetter) => {
    if (regexForPositiveDecimals.test(value)) {
      variableSetter(value);
    } else {
      showMuiSnack(`${variable}: Can only be a postive number. `, 'warning');
    }
  };

  const updateLayer = useCallback(
    (updates) => {
      setLayers((prev) => {
        const next = prev.slice();
        const current = next[index] || {};
        next[index] = { ...current, ...updates };
        return next;
      });
    },
    [setLayers, index],
  );

  const changeZ = useCallback(
    (value) => {
      updateLayer({ z: value });
    },
    [updateLayer],
  );

  const changeSPT = useCallback(
    (value) => {
      updateLayer({ SPT: value });
    },
    [updateLayer],
  );

  const changeUscs = useCallback(
    (value) => {
      updateLayer({ uscs: value });
    },
    [updateLayer],
  );

  const changeFc = useCallback(
    (value) => {
      updateLayer({ fc: value });
    },
    [updateLayer],
  );

  //MUI ALERT
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [severity, setSeverity] = useState('');

  const showMuiSnack = (message, type) => {
    setAlertMessage(message);
    setSeverity(type);
    setOpen(true);
  };

  const hasResults = Boolean(resultsObj);
  const inputValues = resultConst?.inputs || {};
  const outputConstants = resultConst?.outputs || {};
  return (
    <>
      <div className="layer liqLayerRow">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />

        <OutputValue value={layer.id} />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          size="small"
          variant="outlined"
          className="layeredInputMui"
          fullWidth
          value={layer.z ?? ''}
          onChange={(e) => onChangeWarningForPositiveDecimals(e.target.value, 'z', changeZ)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          size="small"
          variant="outlined"
          className="layeredInputMui"
          fullWidth
          value={layer.SPT ?? ''}
          onChange={(e) => onChangeWarningForPositiveDecimals(e.target.value, 'SPT', changeSPT)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          size="small"
          select
          className="layeredInputMui"
          fullWidth
          value={layer.uscs}
          onChange={(e) => changeUscs(e.target.value)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        >
          <MenuItem value="0" disabled>
            Select USCS
          </MenuItem>
          <MenuItem value="1">GW</MenuItem>
          <MenuItem value="2">GP</MenuItem>
          <MenuItem value="3">GM</MenuItem>
          <MenuItem value="4">GC</MenuItem>
          <MenuItem value="5">SW</MenuItem>
          <MenuItem value="6">SP</MenuItem>
          <MenuItem value="7">SM</MenuItem>
          <MenuItem value="8">SC</MenuItem>
          <MenuItem value="9">ML</MenuItem>
          <MenuItem value="10">MH</MenuItem>
          <MenuItem value="11">CL</MenuItem>
          <MenuItem value="12">CH</MenuItem>
        </TextField>
        <OutputValue value={round(outputConstants?.ce, 2)} />
        <OutputValue value={round(outputConstants?.cb, 2)} />
        <OutputValue value={round(outputConstants?.cs, 2)} />
        <OutputValue value={round(resultsObj?.cr, 2)} />
        <OutputValue value={round(resultsObj?.cn, 2)} />
        <OutputValue value={round(resultsObj?.n160, 2)} />
        <TextField
          sx={{
            '& legend': { display: 'none' },
            '& fieldset': { top: 0 },
          }}
          size="small"
          variant="outlined"
          className="layeredInputMui"
          fullWidth
          value={layer.fc ?? ''}
          onChange={(e) => onChangeWarningForPositiveDecimals(e.target.value, 'fc', changeFc)}
          disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
        />
        <OutputValue value={round(resultsObj?.n160cs, 2)} />
        <OutputValue value={resultsObj?.crr === -1 ? 'No Liq.*' : round(resultsObj?.crr, 3)} />
        <OutputValue value={round(resultsObj?.normalStress, 2)} />
        <OutputValue value={round(resultsObj?.effectiveStress, 2)} />
        <OutputValue value={round(resultsObj?.rd, 3)} />
        <OutputValue
          value={resultsObj?.liquefactionResistance === -1 ? 'No Liq.*' : round(resultsObj?.liquefactionResistance, 1)}
        />
        <OutputValue value={round(resultsObj?.shearStrength, 2)} />
        <OutputValue value={resultsObj?.fs === -1 ? 'No Liq.*' : round(resultsObj?.fs, 2)} />
        <OutputValue value={resultsObj?.resultText} />
        <div className="layeredOutputValue">
          <IconButton size="small" onClick={onToggle} aria-label="Toggle calculation details">
            <ExpandMoreIcon
              fontSize="small"
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.15s',
              }}
            />
          </IconButton>
        </div>
      </div>
      <Collapse in={isExpanded} unmountOnExit className="liqDetailsCollapse">
        <div className="liqCalculationDetails">
          {!hasResults ? (
            <div className="liqUpgradeNotice">Run calculation to see details.</div>
          ) : canUserSeeCalculationSteps ? (
            <>
              <div className="liqDetailsTitle">Calculation Details for Layer {layer.id}</div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>e</sub> | Energy correction factor
                </h4>
                <p className="liqDetailCardItem">Energy correction factor is selected based on the hammer type:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Hammer Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>E</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Safety</div>
                    <div className="correctionFactorTableColumn">0.60 - 1.17</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Donut</div>
                    <div className="correctionFactorTableColumn">0.45 - 1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Auto</div>
                    <div className="correctionFactorTableColumn no-border-bottom">0.90 - 1.60</div>
                  </div>
                </div>
                <p className="liqDetailCardItem">
                  Since the hammer type is{' '}
                  {inputValues?.hammerType === 1 ? 'Safety' : inputValues?.hammerType === 2 ? 'Donut' : 'Auto'}, the energy
                  correction factor is used as the average of suggested range, which is:
                </p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>e</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(outputConstants?.ce, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>b</sub> | Borehole diameter correction factor
                </h4>
                <p className="liqDetailCardItem">Borehole diameter correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Borehole Dia.(mm)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>B</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">65 - 115</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">150</div>
                    <div className="correctionFactorTableColumn">1.05</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">200</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.15</div>
                  </div>
                </div>
                <p className="liqDetailCardItem">
                  Since the borehole diameter is{' '}
                  {inputValues?.boreholeDiameter === 1
                    ? '65 - 115 mm'
                    : inputValues?.boreholeDiameter === 2
                      ? '150 mm'
                      : '200 mm'}
                  , the selected correction factor is:
                </p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>b</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(outputConstants?.cb, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>s</sub> | Sampler type correction factor
                </h4>
                <p className="liqDetailCardItem">Sampler type correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Sampler Type</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>S</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Standard Liner (With liner)</div>
                    <div className="correctionFactorTableColumn">1.00</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">Without Liner</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.10 - 1.30</div>
                  </div>
                </div>
                <p className="liqDetailCardItem">
                  Since the sampler type is {inputValues?.samplerType === 1 ? 'Standard Liner (With liner)' : 'Without Liner'},
                  the selected correction factor is:
                </p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>s</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(outputConstants?.cs, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>r</sub> | Rod length correction factor
                </h4>
                <p className="liqDetailCardItem">Rod length correction factor is selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">Rod Length (m)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        C<sub>R</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">0 - 4</div>
                    <div className="correctionFactorTableColumn">0.75</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">4 - 6</div>
                    <div className="correctionFactorTableColumn">0.85</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">6 - 10</div>
                    <div className="correctionFactorTableColumn">0.95</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">&gt; 10</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.00</div>
                  </div>
                </div>
                <p className="liqDetailCardItem">Since test depth is z = {round(resultsObj?.z, 2)} m, selected factor is:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>r</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cr, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>&sigma; | Total Stress at Soil Layer</h4>
                <p className="liqDetailCardItem">Total stress at a given depth is calculated using:</p>
                <math display="block" className="liqDetailCardMath">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mi>&gamma;</mi>
                  <mo>&times;</mo>
                  <mi>z</mi>
                </math>
                <p className="liqDetailCardItem">Substituting the values:</p>
                <math display="block" className="liqDetailCardMath">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mi>{round(inputValues?.gamma, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.z, 2)}</mi>
                </math>
                <math display="block" className="liqDetailCardMath">
                  <mi>&sigma;</mi>
                  <mo>=</mo>
                  <mn>{round(resultsObj?.normalStress, 2)}</mn>
                  <mtext>&nbsp;kPa</mtext>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>&sigma;' | Effective Stress at Soil Layer</h4>
                {Number(inputValues?.gwt) > Number(resultsObj?.z) ? (
                  <>
                    <p className="liqDetailCardItem">Since GWT &gt; z, effective stress at a given depth is calculated using:</p>
                    <math display="block" className="liqDetailCardMath">
                      <mi>&sigma;'</mi>
                      <mo>=</mo>
                      <mi>&gamma;</mi>
                      <mo>&times;</mo>
                      <mi>z</mi>
                    </math>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <math display="block" className="liqDetailCardMath">
                      <mi>&sigma;'</mi>
                      <mo>=</mo>
                      <mi>{round(inputValues?.gamma, 2)}</mi>
                      <mo>&times;</mo>
                      <mi>{round(resultsObj?.z, 2)}</mi>
                    </math>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Since GWT &le; z, effective stress at a given depth is calculated using:</p>
                    <math display="block" className="liqDetailCardMath">
                      <mi>&sigma;'</mi>
                      <mo>=</mo>
                      <mi>GWT</mi>
                      <mo>&times;</mo>
                      <mi>&gamma;</mi>
                      <mo>+</mo>
                      <mo>(</mo>
                      <mi>z</mi>
                      <mo>-</mo>
                      <mi>GWT</mi>
                      <mo>)</mo>
                      <mo>&times;</mo>
                      <mo>(</mo>
                      <mi>&gamma;</mi>
                      <mo>-</mo>
                      <msub>
                        <mi>&gamma;</mi>
                        <mi>w</mi>
                      </msub>
                      <mo>)</mo>
                    </math>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <math display="block" className="liqDetailCardMath">
                      <mi>&sigma;'</mi>
                      <mo>=</mo>
                      <mi>{round(inputValues?.gwt, 2)}</mi>
                      <mo>&times;</mo>
                      <mi>{round(inputValues?.gamma, 2)}</mi>
                      <mo>+</mo>
                      <mo>(</mo>
                      <mi>{round(resultsObj?.z, 2)}</mi>
                      <mo>-</mo>
                      <mi>{round(inputValues?.gwt, 2)}</mi>
                      <mo>)</mo>
                      <mo>&times;</mo>
                      <mo>(</mo>
                      <mi>{round(inputValues?.gamma, 2)}</mi>
                      <mo>-</mo>
                      <mi>{round(outputConstants?.gammaW, 3)}</mi>
                      <mo>)</mo>
                    </math>
                  </>
                )}
                <math display="block" className="liqDetailCardMath">
                  <mi>&sigma;'</mi>
                  <mo>=</mo>
                  <mn>{round(resultsObj?.effectiveStress, 2)}</mn>
                  <mtext>&nbsp;kPa</mtext>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>n</sub> | Overburden pressure correction factor
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>N</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(outputConstants?.cnCoefficient, 2)}</mi>
                  <mo>&times;</mo>
                  <msup>
                    <mrow>
                      <mo>(</mo>
                      <mfrac>
                        <mi>1</mi>
                        <mi>&sigma;'</mi>
                      </mfrac>
                      <mo>)</mo>
                    </mrow>
                    <mn>0.5</mn>
                  </msup>
                  <mo>&le;</mo>
                  <mi>{round(outputConstants?.cnMax, 2)}</mi>
                </math>
                {resultsObj?.cnNotApplicableDueCohesion ? (
                  <>
                    <p className="liqDetailCardItem">Since the soil is cohesive Cn is not applicable and is taken as 1.0.</p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <math display="block" className="liqDetailCardMath">
                      <msub>
                        <mi>C</mi>
                        <mi>N</mi>
                      </msub>
                      <mo>=</mo>
                      <mi>{round(outputConstants?.cnCoefficient, 2)}</mi>
                      <mo>&times;</mo>
                      <msup>
                        <mrow>
                          <mo>(</mo>
                          <mfrac>
                            <mi>1</mi>
                            <mi>{round(resultsObj?.effectiveStress, 2)}</mi>
                          </mfrac>
                          <mo>)</mo>
                        </mrow>
                        <mn>0.5</mn>
                      </msup>
                    </math>
                    {resultsObj?.cnCappedTop && (
                      <>
                        <p className="liqDetailCardItem">
                          Since calculated C<sub>n</sub> is greater than {round(outputConstants?.cnMax, 2)}, it is capped.
                        </p>
                        <math display="block" className="liqDetailCardMath">
                          <msub>
                            <mi>C</mi>
                            <mi>N</mi>
                          </msub>
                          <mo>=</mo>
                          <mi>{round(outputConstants?.cnMax, 2)}</mi>
                        </math>
                      </>
                    )}
                  </>
                )}
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>N</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.cn, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard">
                <h4>
                  (N<sub>1</sub>)<sub>60</sub> | Corrected SPT value (1 atm)
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mrow>
                      <mo>(</mo>
                      <msub>
                        <mi>N</mi>
                        <mi>1</mi>
                      </msub>
                      <mo>)</mo>
                    </mrow>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>N</mi>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>N</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>R</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>S</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>B</mi>
                  </msub>
                  <mo>&times;</mo>
                  <msub>
                    <mi>C</mi>
                    <mi>E</mi>
                  </msub>
                </math>
                <p className="liqDetailCardItem">Substituting the values:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mrow>
                      <mo>(</mo>
                      <msub>
                        <mi>N</mi>
                        <mi>1</mi>
                      </msub>
                      <mo>)</mo>
                    </mrow>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.SPT, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cn, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(resultsObj?.cr, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(outputConstants?.cs, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(outputConstants?.cb, 2)}</mi>
                  <mo>&times;</mo>
                  <mi>{round(outputConstants?.ce, 2)}</mi>
                </math>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mrow>
                      <mo>(</mo>
                      <msub>
                        <mi>N</mi>
                        <mi>1</mi>
                      </msub>
                      <mo>)</mo>
                    </mrow>
                    <mi>60</mi>
                  </msub>
                  <mo>=</mo>
                  <mi>{round(resultsObj?.n160, 2)}</mi>
                </math>
              </div>

              <div className="liqDetailCard liqDetailCardN160f">
                <h4>
                  (N<sub>1</sub>)<sub>60,f</sub> | FC-corrected SPT value
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <p className="liqDetailCardMath">
                  (N<sub>1</sub>)<sub>60,f</sub> = &alpha; + &beta;(N<sub>1</sub>)<sub>60</sub>
                </p>
                <p className="liqDetailCardItem">Where, &alpha; and &beta; are selected from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow correctionFactorTableRow3">
                    <div className="correctionFactorTableColumn">FC (%)</div>
                    <div className="correctionFactorTableColumn">&alpha;</div>
                    <div className="correctionFactorTableColumn">&beta;</div>
                  </div>
                  <div className="correctionFactorTableRow correctionFactorTableRow3">
                    <div className="correctionFactorTableColumn">FC &le; 5</div>
                    <div className="correctionFactorTableColumn">0</div>
                    <div className="correctionFactorTableColumn">1</div>
                  </div>
                  <div className="correctionFactorTableRow correctionFactorTableRow3">
                    <div className="correctionFactorTableColumn">5 &lt; FC &le; 35</div>
                    <div className="correctionFactorTableColumn">
                      <span className="liqInlineMathExpr">
                        e
                        <sup>
                          1.76 - (190 / FC<sup>2</sup>)
                        </sup>
                      </span>
                    </div>
                    <div className="correctionFactorTableColumn">
                      <span className="liqInlineMathExpr">
                        0.99 + FC<sup>1.5</sup> / 1000
                      </span>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow correctionFactorTableRow3">
                    <div className="correctionFactorTableColumn no-border-bottom">FC &gt; 35</div>
                    <div className="correctionFactorTableColumn no-border-bottom">5</div>
                    <div className="correctionFactorTableColumn no-border-bottom">1.2</div>
                  </div>
                </div>
                <p className="liqDetailCardItem">
                  Since FC = {round(resultsObj?.fc, 2)}%, selected parameters are &alpha; = {round(resultsObj?.alfa, 3)} and
                  &beta; = {round(resultsObj?.beta, 3)}.
                </p>
                <p className="liqDetailCardItem">Substituting the values:</p>
                <p className="liqDetailCardMath">
                  (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.alfa, 3)} + {round(resultsObj?.beta, 3)} x{' '}
                  {round(resultsObj?.n160, 2)}
                </p>
                <p className="liqDetailCardMath">
                  (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)}
                </p>
              </div>

              <div className="liqDetailCard liqDetailCardCrr">
                <h4>
                  CRR<sub>M7.5</sub> | Cyclic resistance ratio
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>CRR</mi>
                    <mrow>M7.5</mrow>
                  </msub>
                  <mo>=</mo>
                  <mfrac>
                    <mn>1</mn>
                    <mrow>
                      <mn>34</mn>
                      <mo>-</mo>
                      <msub>
                        <mi>N</mi>
                        <mn>1,60f</mn>
                      </msub>
                    </mrow>
                  </mfrac>
                  <mo>+</mo>
                  <mfrac>
                    <msub>
                      <mi>N</mi>
                      <mn>1,60f</mn>
                    </msub>
                    <mn>135</mn>
                  </mfrac>
                  <mo>+</mo>
                  <mfrac>
                    <mn>50</mn>
                    <msup>
                      <mrow>
                        <mo>(</mo>
                        <mn>10</mn>
                        <msub>
                          <mi>N</mi>
                          <mn>1,60f</mn>
                        </msub>
                        <mo>+</mo>
                        <mn>45</mn>
                        <mo>)</mo>
                      </mrow>
                      <mn>2</mn>
                    </msup>
                  </mfrac>
                  <mo>-</mo>
                  <mfrac>
                    <mn>1</mn>
                    <mn>200</mn>
                  </mfrac>
                </math>
                {Number(resultsObj?.n160cs) < 30 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Substituting N<sub>1,60f</sub> = {round(resultsObj?.n160cs, 2)}:
                    </p>
                    <math display="block" className="liqDetailCardMath">
                      <msub>
                        <mi>CRR</mi>
                        <mrow>M7.5</mrow>
                      </msub>
                      <mo>=</mo>
                      <mfrac>
                        <mn>1</mn>
                        <mrow>
                          <mn>34</mn>
                          <mo>-</mo>
                          <mn>{round(resultsObj?.n160cs, 2)}</mn>
                        </mrow>
                      </mfrac>
                      <mo>+</mo>
                      <mfrac>
                        <mn>{round(resultsObj?.n160cs, 2)}</mn>
                        <mn>135</mn>
                      </mfrac>
                      <mo>+</mo>
                      <mfrac>
                        <mn>50</mn>
                        <msup>
                          <mrow>
                            <mo>(</mo>
                            <mn>10</mn>
                            <mn>{round(resultsObj?.n160cs, 2)}</mn>
                            <mo>+</mo>
                            <mn>45</mn>
                            <mo>)</mo>
                          </mrow>
                          <mn>2</mn>
                        </msup>
                      </mfrac>
                      <mo>-</mo>
                      <mfrac>
                        <mn>1</mn>
                        <mn>200</mn>
                      </mfrac>
                    </math>
                    <math display="block" className="liqDetailCardMath">
                      <msub>
                        <mi>CRR</mi>
                        <mrow>M7.5</mrow>
                      </msub>
                      <mo>=</mo>
                      <mn>{round(resultsObj?.crr, 3)}</mn>
                    </math>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">
                      Since (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)} &ge; 30, soil is considered too dense
                      to liquefy.
                    </p>
                    <p className="liqDetailCardMath">
                      CRR<sub>M7.5</sub> = No Liq.*
                    </p>
                  </>
                )}
              </div>

              <div className="liqDetailCard">
                <h4>
                  r<sub>d</sub> | Stress reduction factor
                </h4>
                <p className="liqDetailCardItem">Stress reduction factor is selected based on depth z from the table below:</p>
                <div className="correctionFactorTable">
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">z (m)</div>
                    <div className="correctionFactorTableColumn">
                      <p>
                        r<sub>d</sub>
                      </p>
                    </div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">z &le; 9.15</div>
                    <div className="correctionFactorTableColumn">1 - 0.00765z</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">9.15 &lt; z &le; 23</div>
                    <div className="correctionFactorTableColumn">1.174 - 0.0267z</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn">23 &lt; z &le; 30</div>
                    <div className="correctionFactorTableColumn">0.744 - 0.008z</div>
                  </div>
                  <div className="correctionFactorTableRow">
                    <div className="correctionFactorTableColumn no-border-bottom">z &gt; 30</div>
                    <div className="correctionFactorTableColumn no-border-bottom">0.5</div>
                  </div>
                </div>
                {resultsObj?.rdCaseId === 4 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Since z = {round(resultsObj?.z, 2)} m &gt; 30 m, stress reduction factor is constant:
                    </p>
                    <p className="liqDetailCardMath">
                      r<sub>d</sub> = 0.5
                    </p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Since z = {round(resultsObj?.z, 2)} m, substituting the values:</p>
                    {resultsObj?.rdCaseId === 1 ? (
                      <p className="liqDetailCardMath">
                        r<sub>d</sub> = 1 - 0.00765 x {round(resultsObj?.z, 2)}
                      </p>
                    ) : resultsObj?.rdCaseId === 2 ? (
                      <p className="liqDetailCardMath">
                        r<sub>d</sub> = 1.174 - 0.0267 x {round(resultsObj?.z, 2)}
                      </p>
                    ) : resultsObj?.rdCaseId === 3 ? (
                      <p className="liqDetailCardMath">
                        r<sub>d</sub> = 0.744 - 0.008 x {round(resultsObj?.z, 2)}
                      </p>
                    ) : (
                      <p className="liqDetailCardMath">
                        r<sub>d</sub> = -
                      </p>
                    )}
                  </>
                )}
                {resultsObj?.rdCaseId !== 4 && (
                  <p className="liqDetailCardMath">
                    r<sub>d</sub> = {round(resultsObj?.rd, 3)}
                  </p>
                )}
              </div>

              <div className="liqDetailCard">
                <h4>
                  C<sub>M</sub> | Earthquake magnitude correction factor
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>M</mi>
                  </msub>
                  <mo>=</mo>
                  <mfrac>
                    <msup>
                      <mn>10</mn>
                      <mn>2.24</mn>
                    </msup>
                    <msup>
                      <msub>
                        <mi>M</mi>
                        <mi>w</mi>
                      </msub>
                      <mn>2.56</mn>
                    </msup>
                  </mfrac>
                </math>
                <p className="liqDetailCardItem">Substituting the values:</p>
                <math display="block" className="liqDetailCardMath">
                  <msub>
                    <mi>C</mi>
                    <mi>M</mi>
                  </msub>
                  <mo>=</mo>
                  <mfrac>
                    <msup>
                      <mn>10</mn>
                      <mn>2.24</mn>
                    </msup>
                    <msup>
                      <mi>{round(inputValues?.mw, 2)}</mi>
                      <mn>2.56</mn>
                    </msup>
                  </mfrac>
                </math>
                <p className="liqDetailCardMath">
                  C<sub>M</sub> = {round(outputConstants?.cm, 2)}
                </p>
              </div>

              <div className="liqDetailCard">
                <h4>
                  &tau;<sub>R</sub> | Liquefaction resistance
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <p className="liqDetailCardMath">
                  &tau;<sub>R</sub> = CRR<sub>M7.5</sub> x C<sub>M</sub> x &sigma;'
                </p>
                {resultsObj?.crr === -1 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Since (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)} &ge; 30, soil is considered too dense
                      to liquefy.
                    </p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>R</sub> = No Liq.*
                    </p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>R</sub> = {round(resultsObj?.crr, 3)} x {round(outputConstants?.cm, 2)} x{' '}
                      {round(resultsObj?.effectiveStress, 2)}
                    </p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>R</sub> = {round(resultsObj?.liquefactionResistance, 1)}
                    </p>
                  </>
                )}
              </div>

              <div className="liqDetailCard">
                <h4>
                  &tau;<sub>eq</sub> | Average cyclic shear stress
                </h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <p className="liqDetailCardMath">
                  &tau;<sub>eq</sub> = 0.65 x &sigma; x (0.4S<sub>DS</sub>) x r<sub>d</sub>
                </p>
                {resultsObj?.crr === -1 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Since (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)} &ge; 30, soil is considered too dense
                      to liquefy.
                    </p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>eq</sub> = No Liq.*
                    </p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>eq</sub> = 0.65 x {round(resultsObj?.normalStress, 2)} x (0.4 x {round(inputValues?.sds, 2)}) x{' '}
                      {round(resultsObj?.rd, 3)}
                    </p>
                    <p className="liqDetailCardMath">
                      &tau;<sub>eq</sub> = {round(resultsObj?.shearStrength, 2)}
                    </p>
                  </>
                )}
              </div>

              <div className="liqDetailCard">
                <h4>FS | Factor of safety</h4>
                <p className="liqDetailCardItem">Calculated using:</p>
                <p className="liqDetailCardMath">
                  FS = &tau;<sub>R</sub> / &tau;<sub>eq</sub>
                </p>
                {resultsObj?.fs === -1 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Since (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)} &ge; 30, soil is considered too dense
                      to liquefy.
                    </p>
                    <p className="liqDetailCardMath">FS = No Liq.*</p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Substituting the values:</p>
                    <p className="liqDetailCardMath">
                      FS = {round(resultsObj?.liquefactionResistance, 1)} / {round(resultsObj?.shearStrength, 2)}
                    </p>
                    <p className="liqDetailCardMath">FS = {round(resultsObj?.fs, 2)}</p>
                  </>
                )}
              </div>

              <div className="liqDetailCard">
                <h4>Result</h4>
                {resultsObj?.fs === -1 ? (
                  <>
                    <p className="liqDetailCardItem">
                      Since (N<sub>1</sub>)<sub>60,f</sub> = {round(resultsObj?.n160cs, 2)} &ge; 30, soil is considered too dense
                      to liquefy.
                    </p>
                    <p className="liqDetailCardMath">Result = No Liq.*</p>
                  </>
                ) : (
                  <>
                    <p className="liqDetailCardItem">Result classification is based on FS:</p>
                    <p className="liqDetailCardMath">If FS &lt; 1.1, Liquefies. If FS &ge; 1.1, No Liquefaction.</p>
                    <p className="liqDetailCardMath">Result = {resultsObj?.resultText || '-'}</p>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="liqUpgradeNoticeFullRow">
              <LockOutlinedIcon fontSize="small" />
              Unlock detailed steps with a paid plan.
              <Link to="/pricing" className="liqUpgradeLink" target="_blank" rel="noreferrer">
                See pricing
              </Link>
            </div>
          )}
        </div>
      </Collapse>
    </>
  );
};

export default React.memo(OutputLayer);
